import "@fontsource/inter/index.css";
import "@fontsource/montserrat/index.css";
import {
  ActionIcon,
  Avatar,
  Badge,
  Button,
  Card,
  Container,
  Divider,
  Grid,
  Group,
  Image,
  Modal,
  Stack,
  Text,
  TextInput,
  useMantineTheme,
} from "@mantine/core";
import {
  IconArrowRight,
  IconBook,
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandLinkedin,
  IconBrandTwitter,
  IconBrandWhatsapp,
  IconBrandYoutube,
  IconClock,
  IconMail,
  IconMapPin,
  IconMenu2,
  IconPhone,
  IconSchool,
  IconTrophy,
  IconUser,
  IconUsers,
  IconVideo,
  IconX,
  IconMath,
  IconAtom,
  IconLanguage,
  IconFlask,
  IconMicroscope,
} from "@tabler/icons-react";
import { motion } from "framer-motion";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { AllFatherContext } from "../../App";
import JumboStudents from "../../Assets/IMG/jumbo-students-2.jpg";
// import JumboStudents from "../../Assets/IMG/jumbo-students.jpg";
import {
  AppDomain,
  AppLocation,
  AppLocationUK,
  Colors,
  ContactInformation,
  SocialMediaLinks,
} from "../../Lib/data";
import LanguageSelector from "../../Lib/i18n/LanguageSelector";

import MegaLoader from "../../Components/MegaLoader";
import "./styles.scss";

const Home: React.FC = () => {
  const navigate = useNavigate();
  const theme = useMantineTheme();
  const { t, i18n } = useTranslation();
  const [mobileMenuOpened, setMobileMenuOpened] = useState(false);

  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [expanded, setExpanded] = useState<number | null>(null);
  const [testimonialExpanded, setTestimonialExpanded] = useState<number | null>(
    null
  );
  const [isSticky, setIsSticky] = useState(false);
  const [loading, setLoading] = useState(true);
  const context = useContext(AllFatherContext);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1200);

  // Enforce light mode for homepage
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", "light");
    if (context && context.setTheme) {
      context.setTheme("light");
    }
    localStorage.setItem("app-theme", "light");
    window.scrollTo(0, 0);
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const hero = document.getElementById("home");
      if (!hero) return;
      const heroBottom = hero.getBoundingClientRect().bottom;
      setIsSticky(heroBottom <= 0);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1200);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1200);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "Medical Student",
      avatar:
        "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
      text: t("home.testimonials.medicalStudent"),
      rating: 5,
    },
    {
      name: "Michael Chen",
      role: "Engineering Student",
      avatar:
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      text: t("home.testimonials.engineeringStudent"),
      rating: 5,
    },
    {
      name: "Emily Rodriguez",
      role: "Law Student",
      avatar:
        "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      text: t("home.testimonials.lawStudent"),
      rating: 5,
    },
  ];

  const stats = [
    {
      label: t("home.stats.activeStudents"),
      value: "50,000+",
      icon: IconUsers,
    },
    {
      label: t("home.stats.expertInstructors"),
      value: "200+",
      icon: IconSchool,
    },
    { label: t("home.stats.liveSessions"), value: "1000+", icon: IconClock },
    { label: t("home.stats.successRate"), value: "95%", icon: IconTrophy },
  ];

  const features = [
    {
      icon: IconVideo,
      title: t("home.features.liveSessions"),
      description: t("home.features.liveSessionsDesc"),
    },
    {
      icon: IconBook,
      title: t("home.features.comprehensiveMaterials"),
      description: t("home.features.comprehensiveMaterialsDesc"),
    },
    {
      icon: IconUsers,
      title: t("home.features.communityLearning"),
      description: t("home.features.communityLearningDesc"),
    },
    {
      icon: IconClock,
      title: t("home.features.flexibleScheduling"),
      description: t("home.features.flexibleSchedulingDesc"),
    },
  ];

  // Teacher and student profiles for the grid
  const profiles = [
    {
      name: "Dr. Sarah Johnson",
      avatar:
        "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
      title: t("home.profiles.1.title"),
      description: t("home.profiles.1.description"),
    },
    {
      name: "Prof. Michael Chen",
      avatar:
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      title: t("home.profiles.2.title"),
      description: t("home.profiles.2.description"),
    },
    {
      name: "Attorney Emily Rodriguez",
      avatar:
        "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      title: t("home.profiles.3.title"),
      description: t("home.profiles.3.description"),
    },
    {
      name: "Sarah Williams",
      avatar:
        "https://images.unsplash.com/photo-1511367461989-f85a21fda167?w=150&h=150&fit=crop&crop=face",
      title: t("home.profiles.4.title"),
      description: t("home.profiles.4.description"),
    },
  ];

  return (
    <>
      <motion.div
        initial={{ opacity: 1 }}
        animate={{
          opacity: loading ? 1 : 0,
          pointerEvents: loading ? "auto" : "none",
        }}
        transition={{ duration: 0.5 }}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          background: "linear-gradient(120deg, #ffffff 50%, #f0f7ff 100%)",
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <MegaLoader size={64} color="violet" type="bars" />
      </motion.div>
      <div
        className="home-container"
        style={{ opacity: loading ? 0 : 1, transition: "opacity 0.5s" }}
      >
        {/* Header */}
        <header className={`header${isSticky ? " sticky-header" : ""}`}>
          <Container size="xl">
            <Group justify="space-between" align="center" h={80}>
              {/* Logo */}
              <Group gap="md">
                <img
                  className="logo"
                  src={context?.assets?.logos?.legacy}
                  alt={t("common.logoAlt")}
                />
              </Group>

              {/* Desktop Navigation */}
              {!isMobile && (
                <Group gap="xl" className="nav-desktop">
                  <Link to="/plans">
                    <Text className="nav-link">{t("navigation.plans")}</Text>
                  </Link>
                  <Link to="/about" className="nav-link">
                    {t("navigation.about")}
                  </Link>
                  <Link to="/blogs" className="nav-link">
                    {t("navigation.blog")}
                  </Link>
                  <Link to="/careers" className="nav-link">
                    {t("navigation.careers")}
                  </Link>
                  <Link to="/events" className="nav-link">
                    {t("navigation.events")}
                  </Link>
                </Group>
              )}

              {/* Header Actions */}
              <Group gap="md" className="header-actions">
                <Group
                  gap="md"
                  className="language-switcher"
                  style={{ marginLeft: "auto" }}
                >
                  <LanguageSelector />
                </Group>

                {context && context?.profile && context?.profile?.id ? (
                  <Link to="/d">
                    <Button
                      variant="outline"
                      radius="xl"
                      size="sm"
                      onClick={() => navigate("/d")}
                    >
                      {t("home.hero.goToDashboard")}
                    </Button>
                  </Link>
                ) : (
                  <>
                    <Link to="/login">
                      <Button
                        variant="outline"
                        radius="xl"
                        size="sm"
                        className="nav-btn nav-btn--green-outline"
                      >
                        {t("auth.signIn")}
                      </Button>
                    </Link>

                    <Link to="/sign-up">
                      <Button
                        radius="xl"
                        size="sm"
                        className="nav-btn nav-btn--green"
                        onClick={() => navigate("/sign-up")}
                      >
                        {t("home.hero.getStarted")}
                      </Button>
                    </Link>
                  </>
                )}
              </Group>

              {/* Mobile Menu Button */}
              {isMobile && (
                <ActionIcon
                  variant="subtle"
                  size="lg"
                  className="mobile-menu-btn"
                  onClick={() => setMobileMenuOpened(true)}
                >
                  <IconMenu2 size={24} />
                </ActionIcon>
              )}
            </Group>
          </Container>
        </header>

        {/* Offset for sticky header (only when sticky) */}
        {isSticky && <div className="header-offset" />}

        {/* Mobile Menu */}
        <Modal
          opened={mobileMenuOpened}
          onClose={() => setMobileMenuOpened(false)}
          fullScreen
          transitionProps={{ transition: "fade", duration: 200 }}
          className="mobile-menu-modal"
          withCloseButton={false}
        >
          <Container size="xl" h="100%">
            <Group justify="space-between" align="center" h={80}>
              <img
                src={context?.assets?.logos?.legacy}
                alt="Emril Academy Logo"
                style={{
                  width: 50,
                  height: 50,
                  borderRadius: 12,
                  objectFit: "contain",
                }}
              />
              <ActionIcon
                variant="subtle"
                size="lg"
                onClick={() => setMobileMenuOpened(false)}
              >
                <IconX size={24} />
              </ActionIcon>
            </Group>
            <Stack gap="xl" mt={60} className="mobile-nav">
              <Link to="/plans">
                <Text className="mobile-nav-link">{t("navigation.plans")}</Text>
              </Link>
              <Link to="/about" className="mobile-nav-link">
                {t("navigation.about")}
              </Link>
              <Link to="/blogs" className="mobile-nav-link">
                {t("navigation.blog")}
              </Link>
              <Link to="/careers" className="mobile-nav-link">
                {t("navigation.careers")}
              </Link>
              <Link to="/events" className="mobile-nav-link">
                {t("navigation.events")}
              </Link>
              <Group gap="md" mt="xl">
                <LanguageSelector />
                <Button
                  variant="outline"
                  radius="xl"
                  size="sm"
                  fullWidth
                  className="nav-btn nav-btn--green-outline"
                  onClick={() => {
                    setMobileMenuOpened(false);
                    navigate("/login");
                  }}
                >
                  {t("auth.signIn")}
                </Button>
                <Button
                  radius="xl"
                  size="sm"
                  fullWidth
                  className="nav-btn nav-btn--green"
                  onClick={() => {
                    setMobileMenuOpened(false);
                    navigate("/sign-up");
                  }}
                >
                  {t("home.hero.getStarted")}
                </Button>
              </Group>
            </Stack>
          </Container>
        </Modal>

        {/* Hero Section */}
        <section id="home" className="hero-section">
          <div className="bubbles-bg">
            <div className="bubble bubble1" />
            <div className="bubble bubble2" />
            <div className="bubble bubble3" />
            <div className="bubble bubble4" />
            <div className="bubble bubble5" />
          </div>
          <div className="hero-bg-blob" />
          <Container size="xl">
            <Grid gutter={60} align="center">
              <Grid.Col span={{ base: 12, md: 6 }}>
                <motion.div
                  initial={{ opacity: 0, x: -50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <Stack gap="xl">
                    <div className="hero-badge">
                      <Badge
                        size="lg"
                        variant="gradient"
                        gradient={{ from: "violet", to: "blue", deg: 90 }}
                        radius="xl"
                        px={16}
                        py={8}
                        style={{
                          fontWeight: 700,
                          boxShadow: "0 2px 12px rgba(139,92,246,0.12)",
                        }}
                      >
                        <span role="img" aria-label="star">
                          ⭐
                        </span>{" "}
                        {t("home.hero.trustedBy")}
                      </Badge>
                    </div>
                    <div className="hero-content">
                      <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                      >
                        <Text
                          fw={900}
                          lh={1.15}
                          className="hero-title"
                          style={{ letterSpacing: "-0.03em" }}
                        >
                          {t("home.hero.title")}
                          <div className="gradient-text">
                            {t("home.hero.liveLearning")}
                          </div>
                        </Text>
                      </motion.div>
                      <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.35 }}
                      >
                        <Text
                          size="lg"
                          c="dimmed"
                          mt="md"
                          className="hero-subtitle"
                        >
                          {t("home.hero.description")}
                        </Text>
                      </motion.div>
                    </div>
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: 0.5 }}
                    >
                      <Group gap="md" className="hero-actions">
                        <Button
                          size="lg"
                          radius="xl"
                          className="primary-btn"
                          onClick={() => navigate("/d")}
                          style={{
                            fontWeight: 700,
                            fontSize: "1.15rem",
                            boxShadow: "0 2px 12px rgba(139,92,246,0.12)",
                          }}
                        >
                          {t("home.hero.startLearningNow")}
                          <IconArrowRight size={20} />
                        </Button>
                      </Group>
                    </motion.div>
                    <Group gap="xl" className="hero-stats">
                      {stats.map((stat, index) => (
                        <motion.div
                          key={stat.label}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.6, delay: index * 0.1 }}
                          className="stat-item"
                        >
                          <Group gap="sm">
                            <stat.icon size={24} color={theme.colors.blue[6]} />
                            <div>
                              <Text size="lg" fw={700} c="blue.6">
                                {stat.value}
                              </Text>
                              <Text size="sm" c="dimmed">
                                {stat.label}
                              </Text>
                            </div>
                          </Group>
                        </motion.div>
                      ))}
                    </Group>
                  </Stack>
                </motion.div>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <motion.div
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  className="hero-visual"
                >
                  <div className="hero-image-container">
                    <Image
                      src={JumboStudents}
                      alt={t("home.hero.imageAlt")}
                      radius="lg"
                      className="hero-image"
                      style={{
                        boxShadow: "0 8px 40px 0 rgba(80,0,120,0.18)",
                        position: "relative",
                        zIndex: 2,
                      }}
                    />
                  </div>
                </motion.div>
              </Grid.Col>
            </Grid>
          </Container>
        </section>

        {/* Features Section */}
        <section className="features-section">
          <Container size="xl">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="section-header"
            >
              <Text
                ta="center"
                size="2rem"
                fw={700}
                mt={30}
                mb={10}
                lh={2}
                className="section-title"
              >
                {t("home.features.title")}
              </Text>
              <Text
                ta="center"
                size="lg"
                c="dimmed"
                className="section-subtitle"
              >
                {t("home.features.description")}
              </Text>
            </motion.div>

            <Grid gutter={40} mt={60}>
              {features.map((feature, index) => (
                <Grid.Col key={feature.title} span={{ base: 12, sm: 6, lg: 3 }}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Card className="feature-card" radius="lg" withBorder>
                      <Stack gap="md" align="center" ta="center">
                        <div className="feature-icon">
                          <feature.icon
                            size={32}
                            color={theme.colors.blue[6]}
                          />
                        </div>
                        <div>
                          <Text size="lg" fw={600} mb="xs">
                            {feature.title}
                          </Text>
                          <Text size="sm" c="dimmed" lh={1.6}>
                            {feature.description}
                          </Text>
                        </div>
                      </Stack>
                    </Card>
                  </motion.div>
                </Grid.Col>
              ))}
            </Grid>
          </Container>
          <br />
          <br />
        </section>

        {/* Promo Offer Section */}
        {context?.isDiscountOngoing && (
          <section className="promo-offer-section">
            <Container size="xl">
              <Grid gutter={60} align="center">
                <Grid.Col span={{ base: 12, md: 7 }}>
                  <Stack gap="lg">
                    <div>
                      <Badge size="lg" radius="xl" className="promo-badge">
                        {t("home.promo.specialOffer")}
                      </Badge>
                    </div>
                    <Text fw={900} lh={1.1} className="promo-title">
                      {t("home.promo.saveAmount")}
                      <br />
                      {t("home.promo.saveFrequency")}
                    </Text>
                    <Text size="lg" className="promo-subtitle">
                      {t("home.promo.enrollmentMessage")}
                      <br />
                      {t("home.promo.enrollmentPeriod")}
                    </Text>
                    <Text size="xl" fw={800} className="promo-price">
                      {t("home.promo.monthlyPrice")}
                    </Text>
                    <Group gap="md" mt="sm">
                      <Link to="/sign-up">
                        <Button radius="xl" size="lg" className="promo-cta">
                          {t("home.hero.getStarted")}
                        </Button>
                      </Link>
                    </Group>
                  </Stack>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 5 }}>
                  <div className="promo-visual">
                    <Image
                      src={JumboStudents}
                      alt={t("home.promo.teacherAssistingStudent")}
                    />
                  </div>
                </Grid.Col>
              </Grid>
            </Container>
          </section>
        )}

        {/* About Us Section */}
        <section id="about" className="about-section">
          <Container size="xl">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="section-header"
            >
              <Text
                ta="center"
                size="2.5rem"
                fw={700}
                className="section-title"
              >
                {t("home.about.title")}
              </Text>
              <Text
                ta="center"
                size="lg"
                c="dimmed"
                mt="md"
                className="section-subtitle"
              >
                {t("home.about.subtitle")}
              </Text>
            </motion.div>
            <Grid gutter={40} mt={60} align="center" justify="center">
              <Grid.Col span={{ base: 12, md: 6 }}>
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.7, delay: 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card
                    className="about-card"
                    radius="xl"
                    shadow="xl"
                    withBorder
                  >
                    <Stack gap="md">
                      <Text size="xl" fw={800} className="about-card-title">
                        {t("home.about.visionTitle")}
                      </Text>
                      <Text size="md" c="dark" lh={1.7}>
                        {t("home.about.vision")}
                      </Text>
                    </Stack>
                  </Card>
                  <Card
                    className="about-card"
                    radius="xl"
                    shadow="xl"
                    withBorder
                    mt="xl"
                  >
                    <Stack gap="md">
                      <Text size="xl" fw={800} className="about-card-title">
                        {t("home.about.missionTitle")}
                      </Text>
                      <Text size="md" c="dark" lh={1.7}>
                        {t("home.about.mission")}
                      </Text>
                    </Stack>
                  </Card>
                </motion.div>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 6 }}>
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.7, delay: 0.2 }}
                  viewport={{ once: true }}
                >
                  <Card
                    className="about-card"
                    radius="xl"
                    shadow="xl"
                    withBorder
                  >
                    <Stack gap="md">
                      <Text size="xl" fw={800} className="about-card-title">
                        {t("home.about.historyTitle")}
                      </Text>
                      <Text size="md" c="dark" lh={1.7}>
                        {t("home.about.history")}
                      </Text>
                    </Stack>
                  </Card>
                  <Card
                    className="about-card"
                    radius="xl"
                    shadow="xl"
                    withBorder
                    mt="xl"
                  >
                    <Stack gap="md">
                      <Text size="xl" fw={800} className="about-card-title">
                        {t("home.about.valuesTitle")}
                      </Text>
                      <Text size="md" c="dark" lh={1.7}>
                        {t("home.about.values")}
                      </Text>
                    </Stack>
                  </Card>
                </motion.div>
              </Grid.Col>
            </Grid>
          </Container>
        </section>

        {/* How it Works Section */}
        <section className="how-it-works-section">
          <Container size="xl">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="section-header"
            >
              <Text
                ta="center"
                size="2.5rem"
                fw={700}
                className="section-title"
              >
                {t("home.howItWorks.title")}
              </Text>
              <Text
                ta="center"
                size="lg"
                c="dimmed"
                mt="md"
                className="section-subtitle"
              >
                {t("home.howItWorks.subtitle")}
              </Text>
            </motion.div>
            <Grid gutter={40} mt={60} align="center" justify="center">
              {[
                {
                  icon: <IconUser size={38} color={theme.colors.violet[5]} />,
                  title: t("home.howItWorks.step1"),
                  description: t("home.howItWorks.step1Desc"),
                },
                {
                  icon: <IconBook size={38} color={theme.colors.violet[5]} />,
                  title: t("home.howItWorks.step2"),
                  description: t("home.howItWorks.step2Desc"),
                },
                {
                  icon: <IconSchool size={38} color={theme.colors.violet[5]} />,
                  title: t("home.howItWorks.step3"),
                  description: t("home.howItWorks.step3Desc"),
                },
              ].map((step, idx) => (
                <Grid.Col key={step.title} span={{ base: 12, sm: 6, md: 4 }}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: idx * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Card
                      className="how-it-works-card"
                      radius="lg"
                      shadow="md"
                      withBorder
                    >
                      <div className="how-it-works-step">{idx + 1}</div>
                      <Stack gap="md" align="center" ta="center">
                        <div className="how-it-works-icon">{step.icon}</div>
                        <Text size="lg" fw={700} c="green.8">
                          {step.title}
                        </Text>
                        <Text size="sm" c="green.8">
                          {step.description}
                        </Text>
                      </Stack>
                    </Card>
                  </motion.div>
                </Grid.Col>
              ))}
            </Grid>
          </Container>
        </section>

        {/* Testimonials Section */}
        <section
          id="testimonials"
          className="display-none testimonials-section"
        >
          <Container size="xl">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="section-header"
            >
              <Text
                ta="center"
                size="2.5rem"
                fw={700}
                className="section-title"
              >
                {t("home.testimonials.title")}
              </Text>
              <Text
                ta="center"
                size="lg"
                c="dimmed"
                mt="md"
                className="section-subtitle"
              >
                {t("home.testimonials.subtitle")}
              </Text>
            </motion.div>
            <Grid gutter={32} mt={60}>
              {testimonials.map((testimonial, idx) => {
                const isLong = testimonial.text.length > 180;
                const isExpanded = testimonialExpanded === idx;
                return (
                  <Grid.Col
                    key={testimonial.name}
                    span={{ base: 12, sm: 6, md: 4 }}
                  >
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: idx * 0.1 }}
                      viewport={{ once: true }}
                    >
                      <Card
                        className={`testimonial-card-dark${
                          isExpanded ? " expanded" : ""
                        }`}
                        radius="lg"
                        shadow="md"
                        withBorder
                      >
                        <Stack gap="md" align="center" ta="center">
                          <Avatar src={""} size={70} radius="xl" />
                          <Text size="lg" fw={700} mt="sm" c="white">
                            {testimonial.name}
                          </Text>
                          <Text size="sm" c="dimmed">
                            {testimonial.role}
                          </Text>
                          <Group gap={2} mt={4}>
                            {[...Array(testimonial.rating)].map((_, i) => (
                              <svg
                                key={i}
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="#facc15"
                                stroke="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                              </svg>
                            ))}
                          </Group>
                          <Text
                            size="md"
                            mt="md"
                            c="gray.2"
                            className="testimonial-text"
                            style={
                              isExpanded
                                ? {
                                    WebkitLineClamp: "unset",
                                    maxHeight: "none",
                                    minHeight: 0,
                                  }
                                : {}
                            }
                          >
                            {testimonial.text}
                          </Text>
                          {isLong && (
                            <Button
                              variant="subtle"
                              size="xs"
                              mt={-8}
                              onClick={() =>
                                setTestimonialExpanded(isExpanded ? null : idx)
                              }
                              style={{ color: "#a78bfa" }}
                            >
                              {isExpanded
                                ? t("common.showLess")
                                : t("common.readMore")}
                            </Button>
                          )}
                        </Stack>
                      </Card>
                    </motion.div>
                  </Grid.Col>
                );
              })}
            </Grid>
          </Container>
        </section>

        {/* Teacher Profiles Section */}
        <div className="display-none">
          <section id="teachers" className="teachers-section">
            <Container size="xl">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                viewport={{ once: true }}
                className="section-header"
              >
                <Text
                  ta="center"
                  size="2.5rem"
                  fw={700}
                  className="section-title"
                >
                  {t("home.teachers.title")}
                </Text>
                <Text
                  ta="center"
                  size="lg"
                  c="dimmed"
                  mt="md"
                  className="section-subtitle"
                >
                  {t("home.teachers.subtitle")}
                </Text>
              </motion.div>
              <Grid gutter={32} mt={60}>
                {profiles.map((profile, idx) => {
                  const isLong = profile.description.length > 180;
                  const isExpanded = expanded === idx;
                  return (
                    <Grid.Col
                      key={profile.name}
                      span={{ base: 12, sm: 6, lg: 3 }}
                    >
                      <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: idx * 0.1 }}
                        viewport={{ once: true }}
                      >
                        <Card
                          className={`teacher-card-dark${
                            isExpanded ? " expanded" : ""
                          }`}
                          radius="lg"
                          shadow="md"
                          withBorder
                        >
                          <Stack gap="md" align="center" ta="center">
                            <Avatar
                              src={profile.avatar}
                              size={80}
                              radius="xl"
                            />
                            <Text size="lg" fw={700} mt="sm" c="white">
                              {profile.name}
                            </Text>
                            <Text size="sm" c="dimmed" fw={600}>
                              {profile.title}
                            </Text>
                            <Text
                              size="sm"
                              c="gray.2"
                              className="teacher-description"
                              style={
                                isExpanded
                                  ? {
                                      WebkitLineClamp: "unset",
                                      maxHeight: "none",
                                      minHeight: 0,
                                    }
                                  : {}
                              }
                            >
                              {profile.description}
                            </Text>
                            {isLong && (
                              <Button
                                variant="subtle"
                                size="xs"
                                mt={-8}
                                onClick={() =>
                                  setExpanded(isExpanded ? null : idx)
                                }
                                style={{ color: "#a78bfa" }}
                              >
                                {isExpanded
                                  ? t("common.showLess")
                                  : t("common.readMore")}
                              </Button>
                            )}
                          </Stack>
                        </Card>
                      </motion.div>
                    </Grid.Col>
                  );
                })}
              </Grid>
            </Container>
          </section>
        </div>

        {/* Courses Section */}
        <section id="courses" className="courses-s"></section>

        {/* Career Paths Section */}
        <section id="career-paths" className="career-paths-section">
          <Container size="xl">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="section-header"
            >
              <Text
                ta="center"
                size="2.5rem"
                fw={700}
                className="section-title"
              >
                {t("home.coursesOffered.title")}
              </Text>
              <Text
                ta="center"
                size="lg"
                c="dimmed"
                mt="md"
                className="section-subtitle"
              >
                {t("home.coursesOffered.subtitle")}
              </Text>
            </motion.div>
            <Grid gutter={32} mt={60}>
              {[
                {
                  icon: <IconMath size={38} color="#ffffff" />,
                  title: t("home.coursesOffered.maths.title"),
                  description: t("home.coursesOffered.maths.description"),
                  gradient: "linear-gradient(135deg, #16a34a 0%, #22c55e 100%)",
                },
                {
                  icon: <IconAtom size={38} color="#ffffff" />,
                  title: t("home.coursesOffered.physics.title"),
                  description: t("home.coursesOffered.physics.description"),
                  gradient: "linear-gradient(135deg, #ef4444 0%, #f97316 100%)",
                },
                {
                  icon: <IconLanguage size={38} color="#ffffff" />,
                  title: t("home.coursesOffered.english.title"),
                  description: t("home.coursesOffered.english.description"),
                  gradient: "linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)",
                },
                {
                  icon: <IconFlask size={38} color="#ffffff" />,
                  title: t("home.coursesOffered.chemistry.title"),
                  description: t("home.coursesOffered.chemistry.description"),
                  gradient: "linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)",
                },
                {
                  icon: <IconMicroscope size={38} color="#ffffff" />,
                  title: t("home.coursesOffered.biology.title"),
                  description: t("home.coursesOffered.biology.description"),
                  gradient: "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
                },
              ].map((course, idx) => (
                <Grid.Col key={course.title} span={{ base: 12, sm: 6, md: 3 }}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: idx * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Card
                      className="course-card"
                      radius="xl"
                      shadow="xl"
                      withBorder
                      style={{
                        background: course.gradient,
                        boxShadow: "0 8px 40px 0 rgba(80,0,120,0.18)",
                        border: "none",
                        color: "#fff",
                        minHeight: 260,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                        overflow: "hidden",
                        backdropFilter: "blur(12px)",
                      }}
                    >
                      <div style={{ marginBottom: 24 }}>{course.icon}</div>
                      <Text
                        size="xl"
                        fw={800}
                        mb="xs"
                        style={{ letterSpacing: "-0.01em" }}
                      >
                        {course.title}
                      </Text>
                      <Text size="sm" c="white" lh={1.6} ta="center">
                        {course.description}
                      </Text>
                    </Card>
                  </motion.div>
                </Grid.Col>
              ))}
            </Grid>
          </Container>
        </section>

        {/* Footer */}
        <footer className="footer-section">
          <Container size="xl">
            {/* Main Footer Content */}
            <div className="footer-main">
              <Grid gutter="xl">
                {/* Company Info */}
                <Grid.Col span={{ base: 12, md: 4 }}>
                  <Group gap="md" align="center" mb="lg">
                    <img
                      src={context?.assets?.logos?.legacy}
                      alt="Emril Academy Logo"
                      className="footer-logo"
                    />
                    <Text fw={700} size="xl" className="footer-title">
                      Emril Academy
                    </Text>
                  </Group>
                  <Text
                    size="sm"
                    c="dimmed"
                    mb="lg"
                    className="footer-description"
                  >
                    {t("footer.tagline")}
                  </Text>

                  {/* Contact Info */}
                  <Stack gap="sm">
                    <Group gap="sm">
                      <IconPhone
                        size={16}
                        color="var(--mantine-color-violet-6)"
                      />
                      <Text size="sm" c="dimmed">
                        +44 7849 389090
                      </Text>
                    </Group>
                    <Group gap="sm">
                      <IconMail
                        size={16}
                        color="var(--mantine-color-violet-6)"
                      />
                      <Text size="sm" c="dimmed">
                        info@{AppDomain}
                      </Text>
                    </Group>
                    <Group gap="sm">
                      <IconMapPin
                        size={16}
                        color="var(--mantine-color-violet-6)"
                      />
                      <Text size="sm" c="dimmed">
                        <b>Nigeria:</b>&nbsp;
                        {AppLocation}
                      </Text>
                    </Group>
                    <Group gap="sm">
                      <IconMapPin
                        size={16}
                        color="var(--mantine-color-violet-6)"
                      />
                      <Text size="sm" c="dimmed">
                        <b>UK:</b>&nbsp;
                        {AppLocationUK}
                      </Text>
                    </Group>
                  </Stack>
                </Grid.Col>

                {/* Quick Links */}
                <Grid.Col span={{ base: 12, sm: 6, md: 2 }}>
                  <Text fw={600} mb="md" className="footer-section-title">
                    {t("footer.company")}
                  </Text>
                  <Stack gap="xs">
                    <Text component="a" href="/about" className="footer-link">
                      {t("footer.aboutUs")}
                    </Text>
                    <Text component="a" href="/careers" className="footer-link">
                      {t("footer.careers")}
                    </Text>
                    <Text component="a" href="/events" className="footer-link">
                      {t("navigation.events")}
                    </Text>
                    <Text component="a" href="/blogs" className="footer-link">
                      {t("navigation.blog")}
                    </Text>
                  </Stack>
                </Grid.Col>

                {/* Learning */}
                <Grid.Col span={{ base: 12, sm: 6, md: 2 }}>
                  <Text fw={600} mb="md" className="footer-section-title">
                    {t("footerExtras.learning")}
                  </Text>
                  <Stack gap="xs">
                    <Text component="a" href="/sign-up" className="footer-link">
                      {t("auth.signUp")}
                    </Text>
                    <Text component="a" href="/plans" className="footer-link">
                      {t("navigation.plans")}
                    </Text>
                    <Text component="a" href="/login" className="footer-link">
                      {t("footerExtras.studentPortal")}
                    </Text>
                  </Stack>
                </Grid.Col>

                {/* Support */}
                <Grid.Col span={{ base: 12, sm: 6, md: 2 }}>
                  <Text fw={600} mb="md" className="footer-section-title">
                    {t("footerExtras.support")}
                  </Text>
                  <Stack gap="xs">
                    <Text
                      component="a"
                      href={`mailto:${ContactInformation.email}`}
                      className="footer-link"
                    >
                      {t("navigation.contact")}
                    </Text>
                    <Text component="a" href="/terms" className="footer-link">
                      {t("footer.termsOfService")}
                    </Text>
                    <Text component="a" href="/privacy" className="footer-link">
                      {t("footer.privacyPolicy")}
                    </Text>
                  </Stack>
                </Grid.Col>

                {/* Social Media */}
                <Grid.Col span={{ base: 12, md: 2 }}>
                  <Text fw={600} mb="md" className="footer-section-title">
                    {t("footerExtras.followUs")}
                  </Text>
                  <Group gap="sm">
                    <ActionIcon
                      variant="gradient"
                      gradient={{ from: "violet", to: "blue" }}
                      size="lg"
                      radius="md"
                      component="a"
                      href={SocialMediaLinks.facebook}
                      target="_blank"
                      className="social-icon"
                    >
                      <IconBrandFacebook size={18} />
                    </ActionIcon>
                    <ActionIcon
                      variant="gradient"
                      gradient={{ from: "blue", to: "cyan" }}
                      size="lg"
                      radius="md"
                      component="a"
                      href={SocialMediaLinks.twitter}
                      target="_blank"
                      className="social-icon"
                    >
                      <IconBrandTwitter size={18} />
                    </ActionIcon>
                    <ActionIcon
                      variant="gradient"
                      gradient={{ from: "pink", to: "purple" }}
                      size="lg"
                      radius="md"
                      component="a"
                      href={SocialMediaLinks.instagram}
                      target="_blank"
                      className="social-icon"
                    >
                      <IconBrandInstagram size={18} />
                    </ActionIcon>

                    <ActionIcon
                      variant="gradient"
                      gradient={{ from: "red", to: "orange" }}
                      size="lg"
                      radius="md"
                      component="a"
                      href={SocialMediaLinks.youtube}
                      target="_blank"
                      className="social-icon"
                    >
                      <IconBrandYoutube size={18} />
                    </ActionIcon>
                  </Group>
                  <Button
                    mt={20}
                    leftSection={
                      <IconBrandWhatsapp color={Colors.White} size={16} />
                    }
                    color={Colors.GreenPrimary}
                    style={{
                      borderRadius: "40px",
                    }}
                    onClick={() =>
                      window.open(`${ContactInformation.whatsapp}`, "_blank")
                    }
                  >
                    <Text c={Colors.White}>Whatsapp</Text>
                  </Button>
                </Grid.Col>
              </Grid>
            </div>

            {/* Footer Bottom */}
            <div className="footer-bottom">
              <Divider mb="lg" color="dark.4" />
              <Group justify="space-between" align="center">
                <Text size="sm" c="dimmed" className="footer-copyright">
                  {t("footer.copyright", { year: new Date().getFullYear() })}
                </Text>
                <Group gap="lg">
                  <Text
                    component="a"
                    href="/terms"
                    size="xs"
                    c="dimmed"
                    className="footer-bottom-link"
                  >
                    {t("footerExtras.termsAndConditions")}
                  </Text>
                  <Text
                    component="a"
                    href="/privacy"
                    size="xs"
                    c="dimmed"
                    className="footer-bottom-link"
                  >
                    {t("footerExtras.privacyPolicy")}
                  </Text>
                  {/* <Text size="xs" c="dimmed">
                    Made with ❤️ by{" "}
                    <a href="https://theajstars.tech" target="_blank" rel="noopener noreferrer">
                      Oluwaferanmi Ajiboye
                    </a>
                  </Text> */}
                </Group>
              </Group>
            </div>
          </Container>
        </footer>
      </div>
    </>
  );
};

export default Home;
